<script setup lang="ts">
/**
 * Текст реплики с разметкой.
 *
 * Отдельный компонент ради троттлинга: поток приносит текст порциями по
 * несколько символов, и разбирать markdown заново на каждую из них — это
 * полный ре-парсинг ответа десятки раз в секунду, растущий вместе с ответом.
 * К концу длинного ответа это уже заметно.
 *
 * Троттлинг с хвостом: последняя порция всегда дорисовывается, потому что
 * таймер ставится от неё же. Обрыв потока посреди слова оставил бы иначе
 * подвисший на экране кусок текста.
 */
import { onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { reportSourceClick } from './api'
import { isInternalHref, renderMarkdown } from './markdown'

const props = defineProps<{ text: string }>()

/** Раз в столько миллисекунд разметка пересобирается. Глазу это незаметно. */
const RENDER_INTERVAL = 120

const html = ref('')

let timer: ReturnType<typeof setTimeout> | undefined

watch(
  () => props.text,
  () => {
    if (timer) return

    timer = setTimeout(() => {
      timer = undefined
      html.value = renderMarkdown(props.text)
    }, RENDER_INTERVAL)
  },
  { immediate: true },
)

onUnmounted(() => clearTimeout(timer))

const router = useRouter()

/**
 * Переход по ключу задачи (DEV-1557).
 *
 * Делегирование на обёртке, а не `RouterLink` в разметке: ссылки рисует
 * `v-html`, компонентов Vue внутри него нет и быть не может. Обычный переход по
 * относительному адресу перезагрузил бы приложение целиком — вместе с панелью,
 * тредом и идущим ответом, которые живут только в памяти.
 *
 * Модификаторы не трогаются: «открыть в новой вкладке» должно работать как
 * везде.
 */
const navigate = (event: MouseEvent) => {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const link = (event.target as Element | null)?.closest?.('a')
  const href = link?.getAttribute('href') ?? ''

  if (!isInternalHref(href)) return

  event.preventDefault()

  // Переход по источнику отмечается в журнале (DEV-1564): по нему видно,
  // пригодились ли ссылки, которые ассистент подставил под ответ. Отметка не
  // ждёт ответа — она не должна задерживать сам переход.
  if (sourceRef(href)) void reportSourceClick(sourceRef(href))

  void router.push(href)
}

/**
 * Что именно открыли: ключ задачи или идентификатор страницы.
 *
 * Разбор по адресу, а не по тексту ссылки: текст пишет модель, и «см. тут»
 * ссылкой на DEV-1592 в журнале не отличить ни от чего.
 */
const sourceRef = (href: string): string => {
  const issue = /^\/issue\/([^/?#]+)/.exec(href)
  if (issue) return decodeURIComponent(issue[1] ?? '')

  const page = /^\/kb\/([^/?#]+)/.exec(href)
  if (page) return decodeURIComponent(page[1] ?? '')

  return ''
}
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- единственный источник — renderMarkdown, он же санитайзер -->
  <div class="amd" v-html="html" @click="navigate" />
</template>

<style scoped>
.amd {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  line-height: 1.45;
  overflow-wrap: anywhere;
}

/* :deep — разметку рисует v-html, и scoped-атрибут на неё не попадает. */
.amd :deep(p) {
  margin: 0 0 8px;
}

.amd :deep(p:last-child) {
  margin-bottom: 0;
}

.amd :deep(ul),
.amd :deep(ol) {
  margin: 0 0 8px;
  padding-left: 20px;
}

.amd :deep(li) {
  margin-bottom: 2px;
}

.amd :deep(h1),
.amd :deep(h2),
.amd :deep(h3),
.amd :deep(h4),
.amd :deep(h5),
.amd :deep(h6) {
  margin: 12px 0 6px;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

.amd :deep(h1:first-child),
.amd :deep(h2:first-child),
.amd :deep(h3:first-child) {
  margin-top: 0;
}

.amd :deep(a) {
  color: var(--color-primary);
  text-decoration: none;
}

.amd :deep(a:hover) {
  text-decoration: underline;
}

/* Ключ задачи узнаётся в тексте начертанием — как в блоке источников 8-2. */
.amd :deep(a[href^='/issue/']) {
  font-weight: var(--font-weight-medium);
}

.amd :deep(code) {
  padding: 1px 4px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
}

/* Длинная строка кода прокручивается внутри блока: иначе она растягивает
   панель, а её ширину человек задал сам перетаскиванием кромки. */
.amd :deep(pre) {
  margin: 0 0 8px;
  padding: 8px 10px;
  overflow-x: auto;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.amd :deep(pre code) {
  padding: 0;
  background: none;
}

.amd :deep(blockquote) {
  margin: 0 0 8px;
  padding-left: 10px;
  border-left: 2px solid var(--color-border);
  color: var(--color-text-secondary);
}

.amd :deep(table) {
  width: 100%;
  margin-bottom: 8px;
  border-collapse: collapse;
  font-size: var(--font-size-caption);
}

.amd :deep(th),
.amd :deep(td) {
  padding: 4px 6px;
  border: 1px solid var(--color-border);
  text-align: left;
}

.amd :deep(th) {
  background: var(--color-bg-muted);
  font-weight: var(--font-weight-semibold);
}

.amd :deep(hr) {
  margin: 12px 0;
  border: none;
  border-top: 1px solid var(--color-border);
}
</style>
